import { finalizeOrder } from 'modules/order/finalize-order/finalize-order-thunk'
import { Frequency } from 'models/RecurringDonationPlan'
import { checkCanMakePayment, checkWalletType } from 'redux/features/stripePaymentRequest'
import { getAnalyticsInstance } from 'modules/analytics/analytics'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { setTransactionId, setTransactionToken } from 'redux/features/transaction'
import { RootState, TypedDispatch } from 'redux/store'
import { trackPurchaseEvents } from '../../analytics/helpers/track-purchase-analytics-event'
import { Organization } from '../../../models/Organization'
import { StudioCampaignResponse } from '../../../models/StudioCampaign'
import { FinalizeOrderArgs } from './utils/types'
import { StatusType } from 'modules/status/state/status-types'
import statusSlice from 'modules/status/state/status-slice'

interface OrderDetails {
  organization: Organization
  campaign: StudioCampaignResponse
  c_src: string | undefined
  c_src2: string | undefined
  referrer: string
  currency: string
  baseAmount: number
  frequency: Frequency
  recurringEndDate: FinalizeOrderArgs['recurringEndDate']
  utmParams: Record<string, string>
  reCaptchaToken?: string
}

interface ProcessFinalizeOrderResult {
  error?: string
  redirectUrl?: string
  transactionId?: number
}

export const processFinalizeOrder = async (
  orderDetails: OrderDetails,
  dispatch: TypedDispatch<RootState>,
): Promise<ProcessFinalizeOrderResult> => {
  const result = await dispatch(finalizeOrder(orderDetails))

  if (finalizeOrder.rejected.match(result)) {
    let error = 'Unknown error occurred while finalizing order.'

    if (typeof result.payload === 'string') {
      error = result.payload
    } else if (result.payload instanceof Error) {
      error = result.payload.message
    } else if (typeof result.payload === 'object' && result.payload !== null) {
      error = JSON.stringify(result.payload)
    } else if (result.error && result.error.message) {
      error = result.error.message
    }

    return { error }
  }

  if (finalizeOrder.fulfilled.match(result)) {
    const analytics = getAnalyticsInstance()

    const { purchase, purchaser, transaction } = result.payload
    const { campaign, organization } = orderDetails

    try {
      await analytics.identify(purchaser.member_id.toString(), {
        hostName: window.location.hostname,
        visitorType: 'member',
      })

      const dwLoaded = dispatch(checkCanMakePayment())
      const walletType = dispatch(checkWalletType())

      await trackPurchaseEvents({
        purchase,
        purchaser,
        campaign: campaign,
        organization: organization,
        dwLoaded,
        walletType,
      })
    } catch (e) {
      Bugsnag.notify(e as NotifiableError)
      console.warn(e)
    }

    if (transaction && 'id' in transaction.data) {
      const transactionId = Number(transaction.data.id)
      dispatch(setTransactionId(Number(transaction.data.id)))
      dispatch(setTransactionToken(transaction.token))

      dispatch(statusSlice.actions.setStatus({ status: StatusType.COMPLETED }))

      return { transactionId }
    }

    dispatch(statusSlice.actions.setStatus({ status: StatusType.COMPLETED }))
  }

  return {}
}

export default processFinalizeOrder
